import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import app from 'durandal/app';
import { Box, CircularProgress, DialogContent, DialogTitle, styled } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirm } from 'material-ui-confirm';
import { Inquiry } from 'persona';
import { InquiryAttributes } from 'persona/dist/lib/interfaces';

import Dialog from '@/components/dialogs/Dialog';
import { ApiService } from '@/features/api';
import { useSyncKoObservable } from '@/hooks/useSyncKoObservable';
import OwnershipInformationService from '@/legacy/components/user/onboardingWizard/company/ownershipInformation/services/ownershipInformationService';
import { Instance as companyService } from '@/legacy/services/company';
import { Instance as userService } from '@/legacy/services/user';
import { Instance as verificationService } from '@/legacy/services/verification/verification';
import { Instance as verificationUserService } from '@/legacy/services/verification/verificationUser';
import {
  isCompanyVerificationCompleted,
  isCompanyVerificationInProgress,
} from '@/utils/verification';

import { isPersonaKycStep } from '../utils/availability';

type PersonaVerificationBindingModel = {
  companyInformation?: {
    address: {
      address: string;
      city: string;
      country: string;
      state: string;
      suite: string;
      zipCode: string;
    };
    businessUrl: string;
    companyId: string;
    corporationType: string;
    name: string;
    operatingName: string;
  };
  userInformation: {
    userId: string;
  };
};

const mapPersonaCorporationType = (corporationType: string) => {
  if (!corporationType) {
    return '';
  }

  const corporationTypeMap: { [key: string]: string } = {
    'Sole Prop': 'Sole Proprietorship',
    Corporation: 'Business Corporation',
    'LLC/Etc': 'Condominium Corporation',
    Charity: 'Nonprofit or Charity (Registered)',
  };

  return corporationTypeMap[corporationType] || '';
};

function mapToPersonaVerificationBindingModel(
  fields: InquiryAttributes,
  companyId: string,
  userId: string,
  isKyc: boolean
): PersonaVerificationBindingModel {
  if (isKyc) {
    return {
      userInformation: {
        userId,
      },
    };
  }
  return {
    companyInformation: {
      companyId,
      name: fields['business-name']?.value,
      operatingName: fields['operating-business-name']?.value,
      corporationType: mapPersonaCorporationType(fields['entity-type']?.value),
      businessUrl: fields['company-website']?.value,
      address: {
        address: fields['business-registered-address-street-1']?.value,
        city: fields['business-registered-address-city']?.value,
        country: fields['business-registered-address-country-code']?.value,
        state: fields['business-registered-address-subdivision']?.value,
        suite: '',
        zipCode: fields['business-registered-address-postal-code']?.value,
      },
    },
    userInformation: {
      userId,
    },
  };
}

const PersonaFlowContainer = styled(Box)(({ theme }) => ({
  '& > iframe': {
    minHeight: '600px',
    minWidth: '400px',
  },
  [theme.breakpoints.down('sm')]: {
    '& > iframe': {
      minWidth: '300px',
    },
  },
}));

type InquiryDialogProps = { isOpen: boolean; onClose: () => void };

export const InquiryDialog = ({ isOpen, onClose }: InquiryDialogProps) => {
  const queryClient = useQueryClient();
  const errorDialogConfirm = useConfirm();
  const companyId = useSyncKoObservable(companyService.CompanyId);
  const companyProfile = useSyncKoObservable(companyService.profile);
  const userId = useSyncKoObservable(userService.UserId);
  const userProfile = useSyncKoObservable(userService.profile);
  const hasCompletedCompanyVerification = useSyncKoObservable(isCompanyVerificationCompleted);
  const hasSubmittedCompanyVerification = useSyncKoObservable(isCompanyVerificationInProgress);
  const referenceId = `${hasCompletedCompanyVerification || hasSubmittedCompanyVerification ? 'kyc' : 'kyb'}:${companyId}:${userId}`;
  let existingInquiryId: string | undefined;
  try {
    existingInquiryId = localStorage.getItem(referenceId);
  } catch (_) {
    // Ignore error
  }
  const { data: ownershipInformation, isLoading: isOwnershipInformationLoading } = useQuery({
    queryFn: OwnershipInformationService.get,
    queryKey: ['company', companyId, 'owners'],
  });
  const userEmail = useSyncKoObservable(userProfile.email);
  const isUbo = useMemo(
    (): boolean =>
      ownershipInformation?.some(
        (owner) => owner.email?.toLowerCase() === userEmail?.toLowerCase()
      ),
    [ownershipInformation, userEmail]
  );
  const { data: companyPersonaMetadata, isFetching: isCompanyPersonaMetadataLoading } = useQuery({
    enabled: isUbo,
    queryFn: async () =>
      ApiService.get<{ caseId: string }>(
        `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/companyPersonaMetadata`
      ),
    queryKey: ['companies', companyId, 'companyPersonaMetadata'],
  });
  const { data: userVerifications, isFetching: isUserVerificationsLoading } = useQuery({
    enabled: isUbo,
    queryFn: async () =>
      Promise.resolve(verificationUserService.GetVerifyIdentityStatus(companyId, userId)),
    queryKey: ['companies', companyId, 'verification', 'users', userId, 'status'],
  });
  const isUboLoading =
    isOwnershipInformationLoading || isCompanyPersonaMetadataLoading || isUserVerificationsLoading;
  const isPersonaKycStepNonUbo = useSyncKoObservable(isPersonaKycStep);
  const shouldRenderKyc = isUbo
    ? (isCompanyVerificationInProgress || isCompanyVerificationCompleted) &&
      !(userVerifications.isIdentitySubmitted || userVerifications.isIdentityVerified)
    : isPersonaKycStepNonUbo;
  const templateId = useMemo(() => {
    if (existingInquiryId) {
      return null;
    }
    if (shouldRenderKyc) {
      return import.meta.env.PERSONA_KYC_TEMPLATE_ID;
    }
    return import.meta.env.PERSONA_GET_STARTED_TEMPLATE_ID;
  }, [existingInquiryId, shouldRenderKyc]);
  const onSuccessfulSubmission = useCallback((): void => {
    queryClient.invalidateQueries(['companies', companyId, 'onboarding', 'status']);
    queryClient.invalidateQueries([
      'companies',
      companyId,
      'verification',
      'outstandingVerificationItems',
    ]);
    app.trigger('onboarding:company:updated');
    app.trigger('onboarding:identity:updated');
    try {
      localStorage.removeItem(referenceId);
    } catch (_) {
      // Ignore error
    }
    onClose();
  }, [companyId, onClose, queryClient, referenceId]);
  const prefilledPersonaFields = useMemo(() => {
    const caseId = companyPersonaMetadata?.caseId;
    const res = caseId ? { caseId } : {};
    if (shouldRenderKyc) {
      return caseId ? res : undefined;
    }
    return {
      ...res,
      email_address: userProfile.email(), // Business/work email address
      form_filler_email_address: userProfile.email(), // User's email address
      business_name: companyProfile.name(),
      business_physical_address_country_code: companyProfile.country(),
      name_first: userProfile.firstName(),
      name_last: userProfile.lastName(),
      phone_number: companyProfile.businessPhone(),
    };
  }, [companyPersonaMetadata?.caseId, companyProfile, userProfile, shouldRenderKyc]);
  const onUnsuccessfulSubmission = useCallback(
    (error: any): void => {
      const errorMessage = error?.message ?? error?.type;
      errorDialogConfirm({
        description: errorMessage ? (
          <FormattedMessage
            defaultMessage="There was an error submitting your verification: {message}. Please try again later."
            values={{ message: errorMessage }}
          />
        ) : (
          <FormattedMessage defaultMessage="There was an error submitting your verification. Please try again later." />
        ),
        hideCancelButton: true,
        title: <FormattedMessage defaultMessage="Error submitting verification" />,
      });
    },
    [errorDialogConfirm]
  );
  const { mutate: submitVerification } = useMutation({
    mutationFn: verificationService.postPersonaVerification,
    onError: onUnsuccessfulSubmission,
    onSuccess: onSuccessfulSubmission,
  });
  const handleComplete = useCallback(
    ({ fields }) => {
      const bindingModel = mapToPersonaVerificationBindingModel(
        fields,
        companyId,
        userId,
        shouldRenderKyc
      );
      submitVerification(bindingModel);
    },
    [companyId, shouldRenderKyc, submitVerification, userId]
  );
  const handleEvent = useCallback(
    (name: string, metadata: Record<string, unknown>): void => {
      if (name === 'start' && metadata.inquiryId && !existingInquiryId) {
        try {
          localStorage.setItem(referenceId, metadata.inquiryId as string);
        } catch (_) {
          // Ignore error
        }
      }
    },
    [existingInquiryId, referenceId]
  );
  return (
    <Dialog fullWidth isOpen={isOpen} maxWidth="xs" onDialogClose={onClose}>
      <DialogTitle sx={{ py: 4 }}>
        <FormattedMessage defaultMessage="Verify your Business and Identity" />
      </DialogTitle>
      <DialogContent>
        {isUboLoading ? (
          <Box alignItems="center" display="flex" justifyContent="center" sx={{ minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <PersonaFlowContainer
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Inquiry
              environmentId={import.meta.env.PERSONA_GET_STARTED_ENVIRONMENT_ID}
              fields={prefilledPersonaFields}
              inquiryId={existingInquiryId}
              onComplete={handleComplete}
              onEvent={handleEvent}
              referenceId={referenceId}
              templateId={templateId}
            />
          </PersonaFlowContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};
